import {
	type FormMetadataAddressLookup,
	type FormMetadataDDS,
	type FormMetadataIkePoleForeman,
	type FormMetadataOCalc,
	type FormMetadataPla,
	type FormMetadataQuickPole,
	type FormMetadataSPIDACalc,
	IkeFieldType,
} from '@ikegps/core-api';
import type { FormModel } from './form';
import type { FormModelField } from './form.field';
import { plaTypeFromMetadata } from './form.pla.type';
import type { FormModelFieldSelectList } from './form.select.list';
import type { FormModelFieldNestedList } from './nested.list/form.nested.list';

export enum IkeCalcVersion {
	/** v0.2.0 - Span / Circuit / Wire */
	V0_2_0 = 512,
	/** v0.2.1 - Span / Circuit / Wire with span-guy mid-span height and WEP height */
	V0_2_1 = 513,
	/** v0.3.0 - Span / Wire */
	V0_3_0 = 768,
}

/**
 * Set a value based of a dot seperated string,
 * eg set('a.b.c', 'value') => { 'a' : { 'b': { 'c': 'value' } } }
 */
const setDotString = (obj: any, key: string, value: any) => {
	var keys = key.split('.');
	var curObj = obj;

	for (var i = 0; i < keys.length - 1; i++) {
		var curKey = keys[i];
		var val = curObj[curKey];
		if (val == null) {
			curObj = curObj[curKey] = {};
		} else {
			curObj = val;
		}
	}

	var lastKey = keys[keys.length - 1];
	curObj[lastKey] = value;
};

export interface FormMetadataStatus {
	/** FieldId of the select list to be used as the status field */
	field: string;
}

export interface FormModelMetadataOptions {
	spidacalc?: FormMetadataSPIDACalc;
	ocalc?: FormMetadataOCalc;
	dds?: FormMetadataDDS;
	poleforeman?: FormMetadataPla;
	ikePoleForeman?: FormMetadataIkePoleForeman;
	quickpole?: FormMetadataQuickPole;
	address_lookup?: FormMetadataAddressLookup;
}

export class FormModelMetadata {
	form: FormModel;
	import: { type: string; version: number } | undefined;
	options: FormModelMetadataOptions = {};
	statusFieldId: string | null = null;
	mapColorFieldId: string | null = null;
	addressLookupFieldId: string | null = null;

	constructor(form: FormModel) {
		this.form = form;
	}

	setImportType(type: string, version: number) {
		this.import = { type, version };
	}

	setMapColorField(field: FormModelField | null) {
		if (!field) {
			this.mapColorFieldId = null;
			return;
		}
		if (field.type !== IkeFieldType.NESTED_LIST && field.type !== IkeFieldType.SELECT_LIST) {
			throw new Error(
				`Map color field: "${field.type}" is not a "${IkeFieldType.NESTED_LIST}" nor a "${IkeFieldType.SELECT_LIST}"`
			);
		}

		this.mapColorFieldId = field.id;
	}

	removeMapColorField(field: FormModelField | null) {
		if (!field) {
			this.mapColorFieldId = null;
		} else if (field.id === this.mapColorFieldId) {
			this.mapColorFieldId = null;
		}
	}

	getMapColorField(): FormModelFieldSelectList | FormModelFieldNestedList | null {
		if (this.mapColorFieldId == null) {
			return null;
		}
		return this.form.getField(this.mapColorFieldId);
	}

	setStatusField(field: FormModelField | null) {
		if (!field) {
			this.statusFieldId = null;
			return;
		}

		if (field.type !== IkeFieldType.NESTED_LIST && field.type !== IkeFieldType.SELECT_LIST) {
			throw new Error(
				`Status field: "${field.type}" is not a "${IkeFieldType.NESTED_LIST}" nor a "${IkeFieldType.SELECT_LIST}"`
			);
		}

		this.statusFieldId = field.id;
	}

	removeStatusField(field: FormModelField | null) {
		if (!field) {
			this.statusFieldId = null;
		} else if (field.id === this.statusFieldId) {
			this.statusFieldId = null;
		}
	}

	getStatusField(): FormModelFieldNestedList | null {
		if (this.statusFieldId == null) {
			return null;
		}
		return this.form.getField(this.statusFieldId);
	}

	setOption(key: string, value: any) {
		setDotString(this.options, key, value);
	}

	setAddressLookupField(field: FormModelField | null) {
		if (!field) {
			this.addressLookupFieldId = null;
			return;
		}
		this.addressLookupFieldId = field.id;
	}

	removeAddressLookupField(field: FormModelField | null) {
		if (!field) {
			this.addressLookupFieldId = null;
			return;
		}
		if (field?.id === this.addressLookupFieldId) {
			this.addressLookupFieldId = null;
		}
	}

	getAddressLookupField(): FormModelField | null {
		if (this.addressLookupFieldId == null) {
			return null;
		}
		return this.form.getFieldsRecursive.find((field) => field.id === this.addressLookupFieldId) ?? null;
	}

	isPLAForm(): boolean {
		return plaTypeFromMetadata(this.options) != null;
	}

	getIkePoleForeman(): FormMetadataIkePoleForeman | null {
		return this.options?.ikePoleForeman ?? null;
	}
}
